/**
 * BulkTagModal — S10-07
 *
 * Modal for bulk-assigning tags to ALL hotspots of a wallet.
 * Opens via "Allen Hotspots Tag zuweisen" button on WalletDetail.
 */
import { useState } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTags } from '../tags/useTags'
import { useBulkTagWalletHotspots } from './useBulkTagWalletHotspots'

type Props = {
  walletId: number
  walletName?: string
  onClose: () => void
  onTagged?: (count: number) => void
}

export function BulkTagModal({ walletId, walletName, onClose, onTagged }: Props) {
  const { t } = useTranslation('wallets')
  const { data: allTags = [] } = useTags()
  const { bulkTag, isPending } = useBulkTagWalletHotspots()
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  function toggleTag(tagId: number) {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  async function handleSubmit() {
    if (selectedTagIds.length === 0) return
    const outcome = await bulkTag(walletId, selectedTagIds, replaceExisting)
    if (outcome.ok) {
      setResult({ type: 'success', message: outcome.message })
      setTimeout(() => {
        onTagged?.(outcome.tagged)
        onClose()
      }, 1500)
    } else {
      setResult({ type: 'error', message: outcome.error })
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="bulk-tag-title">
      <div className="modal-content card card-pad" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="spread" style={{ marginBottom: 16 }}>
          <h3 id="bulk-tag-title">
            {t('detail.tagsForWallet')}
            {walletName && <span className="muted"> — {walletName}</span>}
          </h3>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose} aria-label={t('detail.tagsCloseLabel')}>
            <X size={16} />
          </button>
        </div>

        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          {t('detail.tagsDescription')}
        </p>

        {allTags.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>{t('empty.noTags')}</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {allTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                className={`btn btn-sm ${selectedTagIds.includes(tag.id) ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => toggleTag(tag.id)}
              >
                {selectedTagIds.includes(tag.id) ? '✓ ' : '+ '}{tag.name}
              </button>
            ))}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={replaceExisting}
            onChange={e => setReplaceExisting(e.target.checked)}
          />
          <span className="muted">{t('detail.replaceExistingTags')}</span>
        </label>

        {result && (
          <p
            role="status"
            aria-live="polite"
            className={result.type === 'success' ? 'text-success' : 'text-danger'}
            style={{ fontSize: 13, marginBottom: 12 }}
          >
            {result.message}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSubmit}
            disabled={isPending || selectedTagIds.length === 0}
          >
            {isPending ? t('actions.savingTags') : t('actions.assignTagsCount', { count: selectedTagIds.length })}
          </button>
        </div>
      </div>
    </div>
  )
}
